'use client';

import React, { useState } from 'react';
import { Modal } from '@/components/ui/Modal';
import { ModalHeader } from '@/components/ui/ModalHeader';
import { ModalBody } from '@/components/ui/ModalBody';
import { ModalFooter } from '@/components/ui/ModalFooter';
import { FormGrid } from '@/components/ui/FormGrid';
import { FormField } from '@/components/ui/FormField';
import { TextInput } from '@/components/ui/TextInput';
import { SelectInput } from '@/components/ui/SelectInput';
import { SegmentedControl } from '@/components/ui/SegmentedControl';
import { ExchangeDetailsModal } from '@/components/modals/ExchangeDetailsModal';
import type { ExchangeDetailsData, OperationType, ProductRequest } from '@/types';
import { formatDate } from '@/utils/date';

interface CreateRequestModalProps {
  isOpen: boolean;
  onClose: () => void;
  onAddRequest?: (request: ProductRequest) => void;
}

const users = ['John Doe', 'Sarah Smith', 'Ahmed Hassan'];
const products = ['Rice (Long Grain)', 'Wheat', 'Cotton Seeds', 'Fertilizer (NPK)', 'Joss Powder', 'Bamboo Stick'];

const userData: Record<string, { contactNumber: string }> = {
  'John Doe': { contactNumber: '+1 (555) 123-4567' },
  'Sarah Smith': { contactNumber: '+1 (555) 234-5678' },
  'Ahmed Hassan': { contactNumber: '+1 (555) 345-6789' },
};

export const CreateRequestModal: React.FC<CreateRequestModalProps> = ({
  isOpen,
  onClose,
  onAddRequest,
}) => {
  const [operation, setOperation] = useState<OperationType>('Buy');
  const [selectedUser, setSelectedUser] = useState('');
  const [selectedProduct, setSelectedProduct] = useState('');
  const [quantity, setQuantity] = useState('');
  const [city, setCity] = useState('');
  
  const [requestedProduct, setRequestedProduct] = useState('');
  const [requestedQuantity, setRequestedQuantity] = useState('');
  
  const [showExchangeDetails, setShowExchangeDetails] = useState(false);
  const [exchangeData, setExchangeData] = useState<ExchangeDetailsData | null>(null);

  const generateTransactionId = () => {
    return `REC-${Math.floor(1000 + Math.random() * 9000)}`;
  };

  const getInitials = (name: string) => {
    return name.split(' ').map(n => n[0]).join('').toUpperCase();
  };

  const calculateExchangeRatio = (offeredQty: number, requestedQty: number) => {
    const ratio = requestedQty / offeredQty;
    return `1 : ${ratio.toFixed(1)}`;
  };

  const handleSubmit = () => {
    if (selectedUser && selectedProduct && quantity && city) {
      const newRequest: ProductRequest = {
        userName: selectedUser,
        operation: operation,
        product: selectedProduct,
        quantity: `${parseFloat(quantity).toLocaleString()} kg`,
        amount: operation === 'Exchange' ? `${requestedProduct} (${parseFloat(requestedQuantity).toLocaleString()} kg)` : '₹0.00',
        city: city,
        status: 'Pending',
      };
      onAddRequest?.(newRequest);
    }
    setSelectedUser('');
    setSelectedProduct('');
    setQuantity('');
    setCity('');
    setRequestedProduct('');
    setRequestedQuantity('');
    setOperation('Buy');
    onClose();
  };

  const handlePreviewExchange = () => {
    if (selectedUser && selectedProduct && quantity && requestedProduct && requestedQuantity && city) {
      const user = userData[selectedUser];
      const data: ExchangeDetailsData = {
        transactionId: generateTransactionId(),
        date: formatDate(new Date()),
        requester: {
          name: selectedUser,
          initials: getInitials(selectedUser),
          contactNumber: user?.contactNumber || 'N/A',
          location: city,
        },
        productOffered: {
          name: selectedProduct,
          quantity: parseFloat(quantity),
          unit: 'Kg',
        },
        productRequested: {
          name: requestedProduct,
          quantity: parseFloat(requestedQuantity),
          unit: 'Kg',
        },
        exchangeRatio: calculateExchangeRatio(parseFloat(quantity), parseFloat(requestedQuantity)),
      };
      setExchangeData(data);
      setShowExchangeDetails(true);
    }
  };

  const isExchangeFormValid = selectedUser && selectedProduct && quantity && requestedProduct && requestedQuantity && city;

  const handleExchangeDetailsClose = () => {
    setShowExchangeDetails(false);
    setExchangeData(null);
  };

  return (
    <>
    <Modal isOpen={isOpen} onClose={onClose} size="lg">
      <ModalHeader
        title="Create New Request"
        subtitle="Fill in the details for a new inventory transaction."
        onClose={onClose}
      />

      <ModalBody>
        <FormGrid>
          <FormField label="User Name">
            <SelectInput 
              value={selectedUser}
              onChange={(e) => setSelectedUser(e.target.value)}
            >
              <option value="" disabled>
                Select existing user
              </option>
              {users.map((user) => (
                <option key={user} value={user}>
                  {user}
                </option>
              ))}
            </SelectInput>
          </FormField>

          <FormField label={operation === 'Exchange' ? 'Product Offered' : 'Product Name'}>
            <SelectInput 
              value={selectedProduct}
              onChange={(e) => setSelectedProduct(e.target.value)}
            >
              <option value="" disabled>
                Select Product
              </option>
              {products.map((product) => (
                <option key={product} value={product}>
                  {product}
                </option>
              ))}
            </SelectInput>
          </FormField>
        </FormGrid>

        <div className="mt-4">
          <FormField label="Operation Type">
            <SegmentedControl
              value={operation}
              onChange={(value) => setOperation(value as OperationType)}
              options={[
                { value: 'Buy', label: 'Buy' },
                { value: 'Sell', label: 'Sell' },
                { value: 'Exchange', label: 'Exchange' },
              ]}
            />
          </FormField>
        </div>

        <FormGrid className="mt-4">
          <FormField label={operation === 'Exchange' ? 'Quantity Offered' : 'Quantity'}>
            <TextInput
              type="number"
              min={0}
              placeholder="0.00"
              value={quantity}
              onChange={(e) => setQuantity(e.target.value)}
              suffix={
                <span className="text-[11px] font-semibold uppercase tracking-widest text-gray-400">
                  Kg
                </span>
              }
            />
          </FormField>

          {operation !== 'Exchange' && (
            <FormField label="Amount/Price">
              <TextInput
                type="number"
                min={0}
                placeholder="0.00"
                prefix={<span className="text-sm text-gray-500">₹</span>}
              />
            </FormField>
          )}

          {operation === 'Exchange' && (
            <FormField label="Product Requested">
              <SelectInput 
                value={requestedProduct}
                onChange={(e) => setRequestedProduct(e.target.value)}
              >
                <option value="" disabled>
                  Select Product
                </option>
                {products.filter(p => p !== selectedProduct).map((product) => (
                  <option key={product} value={product}>
                    {product}
                  </option>
                ))}
              </SelectInput>
            </FormField>
          )}
        </FormGrid>

        {operation === 'Exchange' && (
          <div className="mt-4">
            <FormField label="Quantity Requested">
              <TextInput
                type="number"
                min={0}
                placeholder="0.00"
                value={requestedQuantity}
                onChange={(e) => setRequestedQuantity(e.target.value)}
                suffix={
                  <span className="text-[11px] font-semibold uppercase tracking-widest text-gray-400">
                    Kg
                  </span>
                }
              />
            </FormField>
          </div>
        )}

        <div className="mt-4">
          <FormField label="City">
            <TextInput 
              placeholder="Enter city name" 
              value={city}
              onChange={(e) => setCity(e.target.value)}
            />
          </FormField>
        </div>
      </ModalBody>

      {operation === 'Exchange' ? (
        <div className="px-6 pt-2 pb-6">
          <div className="flex gap-3">
            <button
              type="button"
              onClick={handlePreviewExchange}
              disabled={!isExchangeFormValid}
              className="flex-1 border-2 border-blue-500 text-blue-500 hover:bg-blue-50 disabled:opacity-60 disabled:cursor-not-allowed py-3 rounded-xl font-semibold text-sm transition-colors cursor-pointer"
            >
              Exchange Details
            </button>
            <button
              type="button"
              onClick={handleSubmit}
              className="flex-1 bg-blue-500 hover:bg-blue-600 text-white py-3 rounded-xl font-semibold text-sm transition-colors cursor-pointer"
            >
              Submit Request
            </button>
          </div>
          <button
            type="button"
            onClick={onClose}
            className="mt-3 mx-auto block text-xs font-semibold text-gray-400 uppercase tracking-widest cursor-pointer"
          >
            Cancel
          </button>
        </div>
      ) : (
        <ModalFooter
          variant="fullWidthSubmit"
          onCancel={onClose}
          onPrimary={handleSubmit}
          primaryLabel="Submit Request"
        />
      )}
    </Modal>

    {exchangeData && (
      <ExchangeDetailsModal
        isOpen={showExchangeDetails}
        onClose={handleExchangeDetailsClose}
        data={exchangeData}
      />
    )}
    </>
  );
};

